/**
 * Pure helpers for sparse-integer sort_order recompute on drag-drop.
 *
 * No DOM / dnd-kit / React deps — keeps boundary cases (no-op, single
 * element, drag-up vs drag-down, only-changed-rows emission) covered by
 * fast unit tests instead of jsdom + dnd-kit harness.
 *
 * Strategy (Decision: Sparse integer renumber — full group rewrite on drop):
 *   - Reorder the id array by moving `fromIndex` → `toIndex`.
 *   - Re-stamp sort_order = 0, step, 2*step, ... starting at 0.
 *   - Emit one change entry per row whose new sort_order differs from
 *     the implied old sort_order (= oldIndex * step). Rows that keep
 *     their slot produce no entry, which is what the spec requires
 *     (Sparse Integer Sort Order Recompute on Drop / "no op" scenario).
 */

export interface ReorderInput {
  /** Group's current id order, already sorted by sort_order ascending. */
  ids: ReadonlyArray<string>;
  fromIndex: number;
  toIndex: number;
  /** Sparse step. Spec uses 100. */
  step: number;
}

export interface ReorderChange {
  id: string;
  sort_order: number;
}

export interface ReorderResult {
  newOrder: ReadonlyArray<string>;
  changes: ReadonlyArray<ReorderChange>;
}

export function computeReorder(input: ReorderInput): ReorderResult {
  const { ids, fromIndex, toIndex, step } = input;

  // Degenerate cases: empty group or single element.
  if (ids.length <= 1) {
    return { newOrder: ids, changes: [] };
  }

  // Same index → nothing moved.
  if (fromIndex === toIndex) {
    return { newOrder: ids, changes: [] };
  }

  // Build the post-move array.
  const next = ids.slice();
  const [moved] = next.splice(fromIndex, 1);
  if (moved === undefined) {
    return { newOrder: ids, changes: [] };
  }
  next.splice(toIndex, 0, moved);

  // Old positions for diffing — pre-drop the implied sort_order is
  // index * step, since the input array is already sorted by sort_order.
  const oldIndexById = new Map<string, number>();
  ids.forEach((id, i) => oldIndexById.set(id, i));

  const changes: ReorderChange[] = [];
  next.forEach((id, newIdx) => {
    const oldIdx = oldIndexById.get(id);
    if (oldIdx === undefined) return;
    if (oldIdx !== newIdx) {
      changes.push({ id, sort_order: newIdx * step });
    }
  });

  return { newOrder: next, changes };
}
